import { useState, useEffect, useCallback } from 'react';
import { invoicesAPI, paymentsAPI, patientsAPI, doctorAPI } from '../lib/api';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Receipt, RefreshCw, CreditCard, IndianRupee, Eye, Split, Printer, Plus, Trash2 } from 'lucide-react';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  partial: 'bg-blue-100 text-blue-700',
  paid: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-600',
  refunded: 'bg-slate-100 text-slate-600',
};

const printInvoice = (inv) => {
  if (!inv) return;
  const win = window.open('', '_blank', 'width=900,height=900');
  if (!win) { toast.error('Pop-ups blocked. Please allow pop-ups to print.'); return; }
  const items = (inv.items || []).map(it => `
    <tr><td>${it.description || it.name || ''}</td><td style="text-align:center;">${it.quantity || 1}</td><td style="text-align:right;">₹${(it.unit_price || it.price || 0).toFixed(2)}</td><td style="text-align:right;">₹${(it.total || (it.quantity || 1) * (it.unit_price || it.price || 0)).toFixed(2)}</td></tr>
  `).join('');
  const html = `<!DOCTYPE html><html><head><title>Invoice ${inv.invoice_number}</title>
<style>
  body { font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 800px; margin: 24px auto; padding: 24px; color: #1e293b; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #0d9488; padding-bottom: 16px; margin-bottom: 24px; }
  .hosp { font-size: 24px; font-weight: 800; color: #0d9488; }
  .tag { font-size: 12px; color: #64748b; }
  .inv-meta { text-align: right; }
  .inv-meta h1 { font-size: 28px; margin: 0; color: #0d9488; letter-spacing: 2px; }
  .inv-meta .num { font-family: monospace; font-size: 14px; color: #475569; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin: 20px 0; }
  .grid h4 { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin: 0 0 6px 0; }
  .grid p { margin: 2px 0; font-size: 14px; }
  table { width: 100%; border-collapse: collapse; margin-top: 20px; }
  th { background: #f1f5f9; padding: 10px; text-align: left; font-size: 12px; text-transform: uppercase; color: #475569; }
  td { padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 14px; }
  .totals { margin-top: 20px; width: 50%; margin-left: auto; }
  .totals .row { display: flex; justify-content: space-between; padding: 6px 0; }
  .totals .row.total { border-top: 2px solid #0d9488; font-weight: 700; font-size: 18px; color: #0d9488; padding-top: 10px; }
  .footer { margin-top: 40px; text-align: center; color: #94a3b8; font-size: 11px; }
  .badge { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 11px; text-transform: uppercase; font-weight: 600; }
  .badge-paid { background: #d1fae5; color: #047857; }
  .badge-pending { background: #fef3c7; color: #b45309; }
  .badge-partial { background: #dbeafe; color: #1e40af; }
  @media print { body { margin: 0; padding: 16px; } }
</style></head><body>
<div class="head">
  <div>
    <div class="hosp">${inv.hospital_name || 'Klick Niramaya HMS'}</div>
    <div class="tag">Hospital Management System · Tax Invoice</div>
  </div>
  <div class="inv-meta">
    <h1>INVOICE</h1>
    <div class="num">#${inv.invoice_number}</div>
    <div style="margin-top:8px;"><span class="badge badge-${inv.status}">${inv.status}</span></div>
  </div>
</div>
<div class="grid">
  <div>
    <h4>Billed To</h4>
    <p style="font-weight:600;">${inv.patient_name || ''}</p>
    <p>${inv.patient_code || ''}</p>
    <p>${inv.patient_phone || ''}</p>
  </div>
  <div style="text-align:right;">
    <h4>Invoice Details</h4>
    <p><strong>Date:</strong> ${inv.created_at ? new Date(inv.created_at).toLocaleDateString() : ''}</p>
    <p><strong>Doctor:</strong> ${inv.doctor_name || '—'}</p>
    <p><strong>Visit:</strong> ${inv.visit_id || '—'}</p>
  </div>
</div>
<table>
  <thead><tr><th>Description</th><th style="text-align:center;">Qty</th><th style="text-align:right;">Rate</th><th style="text-align:right;">Amount</th></tr></thead>
  <tbody>${items || `<tr><td colspan="4" style="text-align:center;color:#94a3b8;">No line items</td></tr>`}</tbody>
</table>
<div class="totals">
  <div class="row"><span>Subtotal</span><span>₹${(inv.subtotal || 0).toFixed(2)}</span></div>
  ${inv.discount_amount ? `<div class="row"><span>Discount</span><span>-₹${inv.discount_amount.toFixed(2)}</span></div>` : ''}
  ${inv.tax_amount ? `<div class="row"><span>GST</span><span>₹${inv.tax_amount.toFixed(2)}</span></div>` : ''}
  <div class="row total"><span>Total</span><span>₹${(inv.total_amount || 0).toFixed(2)}</span></div>
  ${inv.amount_paid ? `<div class="row"><span>Paid</span><span>₹${inv.amount_paid.toFixed(2)}</span></div>` : ''}
  ${inv.balance ? `<div class="row" style="color:#dc2626;font-weight:600;"><span>Balance Due</span><span>₹${inv.balance.toFixed(2)}</span></div>` : ''}
</div>
<div class="footer">Thank you for choosing ${inv.hospital_name || 'Klick Niramaya HMS'}. For queries, please contact billing department.</div>
<script>window.onload=function(){window.print();setTimeout(function(){window.close()},500);}</script>
</body></html>`;
  win.document.write(html);
  win.document.close();
};

const SplitPaymentDialog = ({ open, onOpenChange, invoice, onUpdated }) => {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('');
  const [ref, setRef] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      fetchPayments();
      setAmount('');
      setMode('');
      setRef('');
      setNotes('');
    }
  }, [open, invoice]);

  const fetchPayments = async () => {
    if (!invoice) return;
    setLoadingPayments(true);
    try {
      const res = await paymentsAPI.getForInvoice(invoice.id);
      setPayments(res.data || []);
    } catch {
      setPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const totalPaid = payments.reduce((s, p) => s + p.amount, 0);
  const remaining = invoice ? Math.max(0, (invoice.total_amount || 0) - totalPaid) : 0;

  const handlePay = async () => {
    if (!mode) { toast.error('Select payment mode'); return; }
    const payAmount = parseFloat(amount);
    if (!payAmount || payAmount <= 0) { toast.error('Enter valid amount'); return; }
    if (payAmount > remaining + 0.01) { toast.error(`Amount exceeds remaining Rs. ${remaining.toFixed(2)}`); return; }

    setLoading(true);
    try {
      await paymentsAPI.addPayment(invoice.id, {
        amount: payAmount,
        payment_mode: mode,
        payment_reference: ref,
        notes,
      });
      toast.success('Payment recorded');
      fetchPayments();
      setAmount('');
      setRef('');
      setNotes('');
      if (payAmount >= remaining - 0.01) {
        onOpenChange(false);
      }
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePayFull = async () => {
    if (!mode) { toast.error('Select payment mode'); return; }
    setLoading(true);
    try {
      await paymentsAPI.addPayment(invoice.id, {
        amount: remaining,
        payment_mode: mode,
        payment_reference: ref,
        notes,
      });
      toast.success('Full payment recorded');
      onOpenChange(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Payment — {invoice.invoice_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Invoice summary */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Total</p>
              <p className="font-bold text-slate-900">Rs. {invoice.total_amount?.toFixed(2)}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg">
              <p className="text-xs text-slate-500">Paid</p>
              <p className="font-bold text-emerald-700">Rs. {totalPaid.toFixed(2)}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="text-xs text-slate-500">Remaining</p>
              <p className="font-bold text-amber-700" data-testid="payment-remaining">Rs. {remaining.toFixed(2)}</p>
            </div>
          </div>

          {/* GST & Discount info */}
          {(invoice.tax_amount > 0 || invoice.discount_amount > 0) && (
            <div className="text-sm text-slate-500 flex gap-4">
              {invoice.tax_amount > 0 && <span>GST ({invoice.tax_percentage}%): Rs. {invoice.tax_amount.toFixed(2)}</span>}
              {invoice.discount_amount > 0 && <span>Discount: Rs. {invoice.discount_amount.toFixed(2)}</span>}
            </div>
          )}

          {/* Payment history */}
          {payments.length > 0 && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-700">Payment History</p>
              {payments.map(p => (
                <div key={p.id} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                  <span className="text-slate-600">{p.payment_mode.toUpperCase()} {p.payment_reference && `(${p.payment_reference})`}</span>
                  <span className="font-medium text-emerald-700">Rs. {p.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {/* New payment form */}
          {remaining > 0.01 && (
            <>
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Add Payment</p>
                <div className="space-y-3">
                  <div>
                    <Label>Payment Mode *</Label>
                    <Select value={mode} onValueChange={setMode}>
                      <SelectTrigger data-testid="payment-mode-select"><SelectValue placeholder="Select mode" /></SelectTrigger>
                      <SelectContent>
                        {['cash', 'card', 'upi', 'online', 'insurance'].map(m => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Amount</Label>
                      <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder={remaining.toFixed(2)} data-testid="payment-amount-input" />
                    </div>
                    <div>
                      <Label>Reference</Label>
                      <Input value={ref} onChange={e => setRef(e.target.value)} placeholder="Txn ID" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handlePay} disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="split-pay-btn">
                      <Split className="h-4 w-4 mr-2" />
                      {loading ? 'Processing...' : 'Pay Partial'}
                    </Button>
                    <Button onClick={handlePayFull} disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="full-pay-btn">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay Full (Rs. {remaining.toFixed(2)})
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const res = await invoicesAPI.list(params);
      setInvoices(res.data);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetch(); }, [fetch]);

  const totals = invoices.reduce((acc, inv) => {
    acc.total += inv.total_amount || 0;
    if (inv.status === 'paid') acc.paid += inv.total_amount || 0;
    if (inv.status === 'pending' || inv.status === 'partial') acc.pending += inv.total_amount || 0;
    return acc;
  }, { total: 0, paid: 0, pending: 0 });

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Invoices</h1>
          <p className="text-sm text-slate-500 mt-1">Manage billing and payment records</p>
        </div>
        <Button onClick={() => setManualOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="manual-invoice-btn">
          <Plus size={14} className="mr-1.5" /> Manual Invoice
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="flex items-center gap-4 p-5">
          <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center"><Receipt size={22} className="text-slate-600" /></div>
          <div><p className="text-2xl font-bold text-slate-900">Rs. {totals.total.toFixed(2)}</p><p className="text-sm text-slate-500">Total Billed</p></div>
        </CardContent></Card>
        <Card><CardContent className="flex items-center gap-4 p-5">
          <div className="h-12 w-12 rounded-lg bg-emerald-50 flex items-center justify-center"><IndianRupee size={22} className="text-emerald-600" /></div>
          <div><p className="text-2xl font-bold text-emerald-700">Rs. {totals.paid.toFixed(2)}</p><p className="text-sm text-slate-500">Collected</p></div>
        </CardContent></Card>
        <Card><CardContent className="flex items-center gap-4 p-5">
          <div className="h-12 w-12 rounded-lg bg-amber-50 flex items-center justify-center"><CreditCard size={22} className="text-amber-600" /></div>
          <div><p className="text-2xl font-bold text-amber-700">Rs. {totals.pending.toFixed(2)}</p><p className="text-sm text-slate-500">Pending</p></div>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Invoice List</CardTitle>
            <div className="flex items-center gap-3">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36"><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {['pending', 'partial', 'paid', 'cancelled', 'refunded'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={fetch}><RefreshCw size={16} /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-400">Loading...</div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-slate-400"><Receipt size={40} className="mb-3 opacity-40" /><p>No invoices found</p></div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold text-slate-700">Invoice #</TableHead>
                    <TableHead className="font-semibold text-slate-700">Patient</TableHead>
                    <TableHead className="font-semibold text-slate-700">Doctor</TableHead>
                    <TableHead className="font-semibold text-slate-700">Amount</TableHead>
                    <TableHead className="font-semibold text-slate-700">GST</TableHead>
                    <TableHead className="font-semibold text-slate-700">Payment Method</TableHead>
                    <TableHead className="font-semibold text-slate-700">Status</TableHead>
                    <TableHead className="font-semibold text-slate-700">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map(inv => (
                    <TableRow key={inv.id} className="hover:bg-slate-50" data-testid={`invoice-row-${inv.id}`}>
                      <TableCell className="font-mono text-sm font-medium text-emerald-700">{inv.invoice_number}</TableCell>
                      <TableCell>
                        <div className="font-medium">{inv.patient_name}</div>
                        <div className="text-xs text-slate-400">{inv.patient_code}</div>
                      </TableCell>
                      <TableCell>{inv.doctor_name || '-'}</TableCell>
                      <TableCell className="font-semibold">Rs. {inv.total_amount?.toFixed(2)}</TableCell>
                      <TableCell>{inv.tax_amount > 0 ? <span className="text-xs">Rs. {inv.tax_amount.toFixed(2)}</span> : '-'}</TableCell>
                      <TableCell data-testid={`invoice-pay-method-${inv.id}`}>
                        {Array.isArray(inv.payment_methods) && inv.payment_methods.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {inv.payment_methods.map(m => (
                              <Badge key={m} className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 font-normal text-[10px]">{m}</Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell><Badge className={STATUS_COLORS[inv.status]}>{inv.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1.5 flex-wrap">
                          {(inv.status === 'pending' || inv.status === 'partial') && (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={() => { setSelectedInvoice(inv); setShowPayment(true); }} data-testid={`pay-invoice-${inv.id}`}>
                              <CreditCard size={12} className="mr-1" /> Pay
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => printInvoice(inv)} data-testid={`print-invoice-${inv.id}`}>
                            <Printer size={12} className="mr-1" /> Print
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <SplitPaymentDialog open={showPayment} onOpenChange={setShowPayment} invoice={selectedInvoice} onUpdated={fetch} />
      <ManualInvoiceDialog open={manualOpen} onOpenChange={setManualOpen} onCreated={() => { fetch(); setManualOpen(false); }} />
    </div>
  );
};

// =================== Manual Invoice Dialog (v37b2) ===================
const ManualInvoiceDialog = ({ open, onOpenChange, onCreated }) => {
  const [patientQuery, setPatientQuery] = useState('');
  const [patients, setPatients] = useState([]);
  const [patient, setPatient] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState('');
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0, amount: 0 }]);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setPatient(null); setPatientQuery(''); setPatients([]);
      setItems([{ description: '', quantity: 1, unit_price: 0, amount: 0 }]);
      setTaxPercentage(0); setDiscount(0); setNotes(''); setDoctorId('');
      doctorAPI.list().then(r => setDoctors(r.data || [])).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (!patientQuery || patientQuery.length < 2) { setPatients([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await patientsAPI.list({ params: { search: patientQuery } });
        setPatients(r.data || []);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [patientQuery]);

  const updateItem = (i, key, val) => {
    setItems(prev => prev.map((it, idx) => {
      if (idx !== i) return it;
      const next = { ...it, [key]: val };
      next.amount = +(Number(next.quantity || 0) * Number(next.unit_price || 0)).toFixed(2);
      return next;
    }));
  };
  const addItem = () => setItems(prev => [...prev, { description: '', quantity: 1, unit_price: 0, amount: 0 }]);
  const removeItem = (i) => setItems(prev => prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, it) => s + Number(it.amount || 0), 0);
  const tax = +(subtotal * taxPercentage / 100).toFixed(2);
  const grand = +(subtotal + tax - Number(discount || 0)).toFixed(2);

  const submit = async () => {
    if (!patient?.id) return toast.error('Pick a patient');
    const cleanItems = items.filter(it => it.description.trim() && it.amount > 0);
    if (cleanItems.length === 0) return toast.error('Add at least one line item');
    setSubmitting(true);
    try {
      const res = await invoicesAPI.create({
        patient_id: patient.id,
        doctor_id: doctorId || null,
        items: cleanItems,
        tax_percentage: Number(taxPercentage || 0),
        discount_amount: Number(discount || 0),
        notes: notes.trim(),
      });
      toast.success(`Invoice ${res.data?.invoice_number || ''} created`);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create invoice');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-invoice-dialog">
        <DialogHeader><DialogTitle>Manual Invoice Entry</DialogTitle>
          <p className="text-xs text-slate-500 mt-1">All fields are audit-logged. Use this for ad-hoc charges not tied to an appointment.</p>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Patient picker */}
          <div>
            <Label>Patient *</Label>
            {patient ? (
              <div className="flex items-center justify-between p-2 rounded border border-emerald-200 bg-emerald-50">
                <div className="text-sm">
                  <strong>{patient.first_name} {patient.last_name}</strong>
                  <span className="text-xs text-slate-500 ml-2">{patient.uhid || patient.patient_id} · {patient.phone}</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setPatient(null)}>Change</Button>
              </div>
            ) : (
              <>
                <Input placeholder="Search patient by name, phone, UHID" value={patientQuery} onChange={e => setPatientQuery(e.target.value)} data-testid="manual-inv-patient-search" />
                {patients.length > 0 && (
                  <div className="mt-1 border border-slate-200 rounded max-h-44 overflow-y-auto">
                    {patients.slice(0, 20).map(p => (
                      <button key={p.id} type="button" onClick={() => { setPatient(p); setPatients([]); }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-emerald-50 border-b border-slate-100">
                        <strong>{p.first_name} {p.last_name}</strong> <span className="text-slate-500">· {p.uhid || p.patient_id} · {p.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          {/* Doctor */}
          <div>
            <Label>Treating Doctor (optional)</Label>
            <Select value={doctorId} onValueChange={setDoctorId}>
              <SelectTrigger data-testid="manual-inv-doctor"><SelectValue placeholder="— Not linked —" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {doctors.map(d => <SelectItem key={d.id} value={d.id}>{d.user?.first_name} {d.user?.last_name} — {d.specialization}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Line Items</Label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="manual-inv-add-item"><Plus size={12} className="mr-1" /> Add Item</Button>
            </div>
            <table className="w-full text-xs border border-slate-200 rounded">
              <thead className="bg-slate-50"><tr>
                <th className="px-2 py-1.5 text-left">Description</th>
                <th className="px-2 py-1.5 text-right w-16">Qty</th>
                <th className="px-2 py-1.5 text-right w-24">Unit ₹</th>
                <th className="px-2 py-1.5 text-right w-24">Amount</th>
                <th className="w-8"></th>
              </tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1"><Input value={it.description} onChange={e => updateItem(i, 'description', e.target.value)} placeholder="e.g., Dressing change" data-testid={`manual-inv-desc-${i}`} /></td>
                    <td className="px-2 py-1"><Input type="number" min={1} value={it.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className="text-right" /></td>
                    <td className="px-2 py-1"><Input type="number" min={0} step="0.01" value={it.unit_price} onChange={e => updateItem(i, 'unit_price', e.target.value)} className="text-right" /></td>
                    <td className="px-2 py-1 text-right font-mono">{it.amount.toFixed(2)}</td>
                    <td className="px-1 text-right">
                      {items.length > 1 && <Button size="icon" variant="ghost" onClick={() => removeItem(i)}><Trash2 size={12} className="text-rose-500" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Tax & discount */}
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Tax %</Label><Input type="number" min={0} max={28} step="0.5" value={taxPercentage} onChange={e => setTaxPercentage(e.target.value)} data-testid="manual-inv-tax" /></div>
            <div><Label>Discount ₹</Label><Input type="number" min={0} step="0.01" value={discount} onChange={e => setDiscount(e.target.value)} data-testid="manual-inv-discount" /></div>
            <div className="rounded bg-emerald-50 border border-emerald-200 p-2 text-right">
              <div className="text-[10px] text-emerald-700 uppercase font-semibold">Grand Total</div>
              <div className="text-xl font-bold text-emerald-700 font-mono">₹ {grand.toFixed(2)}</div>
              <div className="text-[9px] text-slate-500">Sub ₹{subtotal.toFixed(2)} · Tax ₹{tax.toFixed(2)}</div>
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes for this invoice" data-testid="manual-inv-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !patient} className="bg-emerald-600 hover:bg-emerald-700" data-testid="manual-inv-submit">
            {submitting ? 'Creating…' : `Create Invoice (₹${grand.toFixed(2)})`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InvoicesPage;
